import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocalStorage } from "react-use";
import { AUTH_TOKEN_KEY, TENANT_KEY } from "../../../constants/index.ts";

/**
 * This hook performs the following login tasks, given an tenant:
 *
 * 1. Checks for an existing tenant in local storage.
 * 2. If there's an existing tenant and it's different from the current tenant, then
 *    clear the API token and invalidate all queries.
 * 3. Sets the tenant inside local storage for use in the queries in `api/`
 */
export function useSetTenant(tenant: string | null) {
  const [initialTenant, setInitialTenant] = useLocalStorage<string>(
    TENANT_KEY,
    undefined,
    { raw: true },
  );

  const queryClient = useQueryClient();

  const originalTenantRef = useRef(initialTenant);

  useEffect(() => {
    if (
      originalTenantRef.current &&
      tenant &&
      originalTenantRef.current !== tenant
    ) {
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      queryClient
        .invalidateQueries({ queryKey: [] })
        .catch((error) => console.error("Failed to invalidate queries", error));
    }

    if (tenant) {
      setInitialTenant(tenant);
    }

    if (tenant) {
      const cssLinkElement = document.getElementById(
        "tenant-theme-css",
      ) as HTMLLinkElement | null;

      if (cssLinkElement) {
        const currentHref = cssLinkElement.getAttribute("href") ?? "";
        const currentTenant = new URLSearchParams(
          currentHref.split("?")[1],
        ).get("tenant");

        if (currentTenant !== tenant) {
          cssLinkElement.setAttribute(
            "href",
            `/api/theme.css?tenant=${encodeURIComponent(tenant)}`,
          );
        }
      }
    }
  }, [tenant, queryClient, setInitialTenant]);
}
